import React, { useState, useEffect, useCallback, useContext } from 'react'
import { SessionDataProvider } from './context/SessionDataContext'
import { TelemetryDataProvider, TelemetryDataContext } from './context/TelemetryDataContext'
import { StudentAnalyticsProvider } from './context/StudentAnalyticsContext'
import { ActivityProvider } from './context/ActivityContext'
import { SurveyDataProvider } from './context/SurveyDataContext'
import SessionLobby from './components/SessionLobby'
import InstructorDashboard from './InstructorDashboard'
import PresenterTab from './components/presenterTab'
import CodeIssuePanel from '../topBar/CodeIssuePanel'
import PresenterPanel from '../commonUnit/PresenterPanel'
import { TestPanelContextProvider } from '../../context/TestPanelContext'
import { getAllUsersInSession } from '../../service/userService'

// Reads TelemetryDataContext's `refresh` directly rather than remounting the provider
// (e.g. via a `key` prop) - a remount would also unmount SessionLobby below, since it
// lives inside this same provider tree, resetting its session timer as a side effect of
// a button that's only supposed to refresh telemetry-derived tabs. See the incident this
// came from: an earlier version of this file gave TelemetryDataProvider a refetchKey-based
// `key`, and clicking Refetch while on the Live sub-tab reset the timer back to 15:00.
function RefetchButton() {
  const { refresh } = useContext(TelemetryDataContext)
  return (
    <button
      onClick={refresh}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '6px 14px',
        borderRadius: '8px',
        border: '1px solid #d1d5db', background: 'white',
        fontSize: '12px', fontWeight: 500, color: '#555',
        cursor: 'pointer',
      }}
    >
      Refetch
    </button>
  )
}

// Small "i" badge that shows a plain-language description of a Monitor sub-tab on
// hover/focus. Purely informational - lives next to (not inside) the tab button so
// hovering it never triggers a tab switch, and doesn't depend on any external
// stylesheet, matching this file's all-inline-style convention.
// `align`: which edge of the bubble to anchor under the icon. 'center' (the default)
// puts half the bubble's width to the left of the icon - fine for tabs with room on
// both sides, but for the leftmost tab (Live) that pushes the bubble off the left edge
// of the screen. 'left' anchors the bubble's left edge to the icon instead (it only
// grows rightward); 'right' is the mirror, for a tab near the right edge.
function InfoTooltip({ text, align = 'center' }) {
  const [visible, setVisible] = useState(false)
  const bubblePosition =
    align === 'left' ? { left: 0 }
    : align === 'right' ? { right: 0 }
    : { left: '50%', transform: 'translateX(-50%)' }
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      <span
        tabIndex={0}
        role="img"
        aria-label="info"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '14px',
          height: '14px',
          borderRadius: '50%',
          border: '1px solid #bbb',
          color: '#999',
          fontSize: '10px',
          fontStyle: 'italic',
          fontWeight: 700,
          lineHeight: 1,
          cursor: 'help',
          flexShrink: 0,
        }}
      >
        i
      </span>
      {visible && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            top: '22px',
            ...bubblePosition,
            width: '220px',
            padding: '8px 10px',
            borderRadius: '6px',
            background: '#1f2937',
            color: 'white',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: 1.4,
            textAlign: 'left',
            zIndex: 20,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            pointerEvents: 'none',
          }}
        >
          {text}
        </span>
      )}
    </span>
  )
}

// The single "Monitor" tab an instructor watches during class - three sub-tabs, each a
// distinct *mode* rather than a distinct data source:
// - "Session Control" - things an instructor DOES to run the class: open the join link,
//   start/stop the timer, and group students for pairing/broadcast activities. (Merges
//   the old separate "Live" and "Pairing" tabs - both were instructor actions, not things
//   to read, so splitting them across two unrelated-looking tabs was the wrong axis.)
// - "Insights" - how the class is doing right now, live: a per-student pass/fail feed
//   (submission-driven, real-time via websocket) and class-wide charts (telemetry-driven,
//   polled). (Merges the old separate "Performance" and "Analytics" tabs - both answered
//   "how's the class doing," just from two data sources, which read as two disagreeing
//   dashboards rather than one.)
// - "Replay" - a fundamentally different mode (scrubbing back through one student's full
//   session, not a live view), so it stays on its own.
// Insights/Replay used to be disabled outright while the session was active, which
// defeated the point of watching live - that gate is gone; both are backed by
// TelemetryDataProvider's `live` polling (see context/TelemetryDataContext.js) instead of
// a one-time fetch.
//
// `isActive`: whether this Monitor tab is the one currently selected in the parent
// (TabPanels.jsx / GuiExerciseEditor.jsx). This whole panel stays mounted via keepMounted
// so the Session Control tab's timer survives switching to a different top-level tab
// (e.g. Task and Unit Test) - `isActive` just prevents telemetry polling while the
// instructor isn't actually looking at this tab.
//
// Two optional props for pages that embed this Monitor (the experiment page):
//   lobbyNotice   passed to SessionLobby as its `notice`
//   focusRequest  { studentId, nonce } - opens Insights with that student's detail; a new
//                 nonce repeats the request for the same student
//   onFocusHandled  called once the detail is open, so the page can drop the request
const InstructorDashboardPanel = ({ sessionId, isActive = true, lobbyNotice, focusRequest = null, onFocusHandled }) => {
  const [activeTab, setActiveTab] = useState('control')
  // Which half of the merged Insights tab is showing - the two data sources (live
  // submission feed vs. polled telemetry charts) can't usefully render stacked in one
  // scroll (the feed is a full multi-pane layout on its own), so Insights is one tab with
  // an inner switch rather than both rendered at once.
  const [insightsView, setInsightsView] = useState('charts')
  // Which half of Session Control is showing. Defaults to 'overview' so PresenterPanel
  // (Pairing) doesn't mount just from opening Monitor - it used to be its own top-level
  // tab, only reached by a deliberate click, but here Session Control (and this default)
  // is what's shown first. Mounting it unconditionally had two problems: PresenterPanel
  // sets the app-wide ModeContext flag to true on mount (see TestList.jsx), which used to
  // only happen when an instructor deliberately opened Pairing and now would flip for
  // every instructor just from opening Monitor; and its workspace-style layout doesn't
  // fit stacked below SessionLobby's content (see the Pairing toggle button below, and
  // PresenterPanel.jsx's own height comment).
  const [controlView, setControlView] = useState('overview')
  const [students, setStudents] = useState([])

  useEffect(() => {
    if (!focusRequest?.studentId) return
    setActiveTab('insights')
    setInsightsView('charts')
  }, [focusRequest])

  // Only poll telemetry live while Insights or Replay is open, this Monitor tab is the
  // one selected, and (inside the provider) the browser tab itself is visible - see the
  // interval in TelemetryDataContext.js.
  const live = isActive && (activeTab === 'insights' || activeTab === 'replay')

  const fetchStudents = useCallback(async () => {
    if (!sessionId) return
    const users = await getAllUsersInSession(sessionId)
    // role 3 = anonymous guest, role 4 = a real, self-registered student account - both
    // are students and should show up here, for any session type. (This used to fall
    // back to filtering by demographic-survey completion for non-GUI sessions - a
    // leftover from one specific CHI study deployment that left this list empty for
    // every other session, GUI or not.)
    setStudents((users || []).filter(u => u.role === 3 || u.role === 4))
  }, [sessionId])

  useEffect(() => {
    fetchStudents()
  }, [fetchStudents])

  // Re-poll the roster on the same cadence as telemetry (and fire once immediately
  // rather than waiting out the first interval) - Analytics/Replay filter telemetry
  // against this list, so a student who joins after the instructor loaded this page
  // would otherwise never appear even though their telemetry is polling in live.
  useEffect(() => {
    if (!live) return
    fetchStudents()
    const interval = setInterval(fetchStudents, 20000)
    return () => clearInterval(interval)
  }, [live, fetchStudents])

  const tabs = [
    {
      key: 'control',
      label: 'Session Control',
      description: 'Everything you do to run the class: the join link, student count, session timer, and grouping/broadcast controls for pairing activities. Start here.',
    },
    {
      key: 'insights',
      label: 'Insights',
      description: 'How the class is doing right now: a live per-student pass/fail feed plus class-wide charts (error trends, AI usage, pass rate, recent AI conversations). The charts refresh automatically about every 20 seconds while open.',
    },
    {
      key: 'replay',
      label: 'Replay',
      description: "Step back through an individual student's coding session, grouped by the type of error they ran into - useful for picking who to spotlight or debug with the class.",
    },
  ]

  return (
    <SessionDataProvider sessionId={sessionId}>
      <TelemetryDataProvider live={live}>
        <StudentAnalyticsProvider>
          <ActivityProvider sessionId={sessionId}>
            <SurveyDataProvider sessionId={sessionId}>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                backgroundColor: '#f5f5f5',
                padding: '8px 20px 0 20px',
                alignSelf: 'stretch',
              }}>
                <div style={{ display: 'flex', gap: '0' }}>
                  {tabs.map((tab, i) => (
                    <div
                      key={tab.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '8px 14px 8px 20px',
                        borderBottom: activeTab === tab.key ? '2px solid #0070CC' : '2px solid transparent',
                      }}
                    >
                      <button
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                          padding: 0,
                          border: 'none',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          color: activeTab === tab.key ? '#0070CC' : '#999',
                        }}
                      >
                        {tab.label}
                      </button>
                      <InfoTooltip
                        text={tab.description}
                        align={i === 0 ? 'left' : i === tabs.length - 1 ? 'right' : 'center'}
                      />
                    </div>
                  ))}
                </div>

                {(activeTab === 'insights' || activeTab === 'replay') && (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                    <button
                      onClick={() => window.open(`/workspace/${sessionId}`, '_blank')}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '6px 14px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db', background: 'white',
                        fontSize: '12px', fontWeight: 500, color: '#555',
                        cursor: 'pointer',
                      }}
                    >
                      Open Workspace
                    </button>
                    <RefetchButton />
                  </div>
                )}
              </div>

              {/* Whether this sub-tab's content needs a real, fixed height (so a
                  `height:'100%'` child - Replay's root, or Pairing's - has something
                  concrete to fill) rather than just `minHeight` (so content that can
                  legitimately grow past 70vh, like the Overview view, doesn't get clipped).
                  See the callout on `controlView` above and the height comment below for
                  the bug this distinction fixes. */}
              {(() => {
                const isFixedHeightView = activeTab === 'replay' || (activeTab === 'control' && controlView === 'pairing')
                return (
              <div style={{
                ...(isFixedHeightView ? { height: '70vh', display: 'flex', flexDirection: 'column' } : { minHeight: '70vh' }),
                overflow: 'hidden',
                padding: activeTab === 'replay' ? '12px' : '0 0 40px 0',
                alignSelf: 'stretch',
              }}>
                {/* Kept mounted (hidden via CSS) rather than conditionally rendered - the
                    session timer is SessionLobby's own local React state, with no
                    server-side persistence, which would reset every time the instructor
                    switched to a different sub-tab and back if this were unmounted in
                    between (the same class of bug the outer keepMounted on this whole
                    panel fixes one level up). Nothing above this in the tree carries a
                    `key` prop either, for the same reason - see RefetchButton's comment.
                    Hidden (not just skipped) whenever Pairing is showing, not only when a
                    different top-level tab is active - previously SessionLobby stayed
                    visible above Pairing regardless of which Session Control sub-view was
                    selected, squeezing Pairing's own workspace into a small leftover strip
                    that needed its own separate inner scroll ("the entire window is not
                    viewable"). Pairing now gets the full sub-tab body to itself. */}
                <div style={{ display: activeTab === 'control' && controlView === 'overview' ? 'block' : 'none' }}>
                  <SessionLobby sessionId={sessionId} notice={lobbyNotice} />
                </div>
                {/* Pairing controls, merged into the same tab as the join link/timer above
                    since both are things an instructor does to run the class rather than
                    things to watch. Behind its own inner toggle (defaulting to Overview,
                    i.e. not rendered) rather than always-on - see controlView's comment
                    above for why. Not kept mounted when switched away - PresenterPanel
                    held no cross-tab state before this merge either. */}
                {activeTab === 'control' && (
                  <div style={{
                    padding: '24px 20px 0 20px',
                    ...(controlView === 'pairing' ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, paddingBottom: '12px' } : {}),
                  }}>
                    <div style={{ display: 'flex', gap: '0', borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginBottom: '4px', flexShrink: 0 }}>
                      {[{ key: 'overview', label: 'Overview' }, { key: 'pairing', label: 'Pairing' }].map(view => (
                        <button
                          key={view.key}
                          onClick={() => setControlView(view.key)}
                          style={{
                            padding: '6px 14px',
                            border: 'none',
                            borderBottom: controlView === view.key ? '2px solid #0070CC' : '2px solid transparent',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                            color: controlView === view.key ? '#0070CC' : '#999',
                          }}
                        >
                          {view.label}
                        </button>
                      ))}
                    </div>
                    {controlView === 'overview' && (
                      <p style={{ fontSize: '13px', color: '#888', margin: '12px 0 0 0' }}>
                        Use the Pairing tab above to group students for a pairing/broadcast activity.
                      </p>
                    )}
                    {controlView === 'pairing' && (
                      <div style={{ flex: 1, overflow: 'hidden', marginTop: '12px', minHeight: 0 }}>
                        <PresenterPanel />
                      </div>
                    )}
                  </div>
                )}
                {activeTab === 'insights' && (
                  <div>
                    <div style={{ display: 'flex', gap: '0', padding: '0 20px', marginBottom: '4px' }}>
                      {[{ key: 'charts', label: 'Charts' }, { key: 'feed', label: 'Live Feed' }].map(view => (
                        <button
                          key={view.key}
                          onClick={() => setInsightsView(view.key)}
                          style={{
                            padding: '6px 14px',
                            border: 'none',
                            borderBottom: insightsView === view.key ? '2px solid #0070CC' : '2px solid transparent',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                            color: insightsView === view.key ? '#0070CC' : '#999',
                          }}
                        >
                          {view.label}
                        </button>
                      ))}
                    </div>
                    {/* Both sides are kept mounted (CSS-hidden) while on this tab, rather
                        than conditionally rendered, so toggling between Charts and Live
                        Feed doesn't lose CodeIssuePanel's resizable-panel widths or
                        re-trigger its loading state every time. */}
                    <div style={{ display: insightsView === 'charts' ? 'block' : 'none' }}>
                      <InstructorDashboard students={students} focusRequest={focusRequest} onFocusHandled={onFocusHandled} />
                    </div>
                    <div style={{ display: insightsView === 'feed' ? 'block' : 'none' }}>
                      <TestPanelContextProvider>
                        <CodeIssuePanel />
                      </TestPanelContextProvider>
                    </div>
                  </div>
                )}
                {activeTab === 'replay' && (
                  <PresenterTab students={students} />
                )}
              </div>
                )
              })()}

            </SurveyDataProvider>
          </ActivityProvider>
        </StudentAnalyticsProvider>
      </TelemetryDataProvider>
    </SessionDataProvider>
  )
}

export default InstructorDashboardPanel
